'use client'

import Image from "next/image";
import Link from "@/components/ui/Link";
import { sanitizeHTML } from "@/lib/utils";
import { getReadableImageUrl } from "@/lib/directus";
import { useEffect, useRef, useState } from "react";

interface FacialSurgeonData {
  heading?: {
    text?: string;
    title?: string;
  };
  subheading?: {
    text?: string;
    title?: string;
  };
  subheading2?: {
    text?: string;
    title?: string;
  };
  text?: {
    text?: string;
  };
  image?: {
    alt?: string;
    image_file?: {
      image?: {
        id?: string;
        filename_download?: string;
      };
    };
  };
  button?: {
    label?: string;
    href?: string;
  };
  images?: {
    images_id?: {
      alt?: string;
      image_file?: {
        image?: {
          id?: string;
          filename_download?: string;
        };
      };
    };
  }[];
}

export const fields = [
  "heading.text",
  "heading.title",
  "subheading.text",
  "subheading.title",
  "subheading2.text",
  "subheading2.title",
  "text.text",
  "image.alt",
  "image.image_file.image.id",
  "image.image_file.image.filename_download",
  "button.label",
  "button.href",
  "images.images_id.alt",
  "images.images_id.image_file.image.id",
  "images.images_id.image_file.image.filename_download"
] as const;

export default function FacialSurgeon({ data }: { data?: FacialSurgeonData }) {
  const logosRef = useRef<HTMLDivElement>(null);
  const [logosVisible, setLogosVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setLogosVisible(entry.isIntersecting),
      { threshold: 0.15, rootMargin: "-50px" }
    );
    const current = logosRef.current;
    if (current) observer.observe(current);
    return () => { if (current) observer.unobserve(current); };
  }, []);

  const imageUrl = getReadableImageUrl(
    data?.image?.image_file?.image?.id || "",
    data?.image?.image_file?.image?.filename_download || ""
  );

  return (
    <section className="FacialSurgeon py-10 md:py-16">
      <div className="container">
        <div className="flex flex-col lg:flex-row lg:space-x-8 mx-auto justify-center gap-8 md:gap-16 lg:items-stretch">
          <div className="order-1 lg:order-1 w-full lg:w-1/2">
            {data?.image && imageUrl && (
              <div className="relative w-full aspect-[3/4] sm:aspect-[4/3] lg:aspect-auto lg:h-full lg:min-h-[400px] overflow-hidden rounded-sm">
                <Image
                  src={imageUrl}
                  alt={data.image.alt || ""}
                  fill
                  quality={95}
                  loading="lazy"
                  className="object-cover object-top"
                  sizes="(min-width: 1024px) 50vw, 100vw"
                />
              </div>
            )}
          </div>
          <div className="order-2 lg:order-2 w-full lg:w-1/2 relative flex flex-wrap content-between">
            <div>
              {data?.subheading && (
                <div className="font-aileron font-bold text-sm uppercase tracking-wider text-secondary-dark mb-3 md:mb-4">
                  {data.subheading.title || data.subheading.text}
                </div>
              )}
              {data?.heading && (
                <h2 className="mb-2">{data.heading.title || data.heading.text}</h2>
              )}
              {data?.subheading2 && (
                <span className="text-base md:text-lg mb-3 block md:mb-8">
                  {data.subheading2.title || data.subheading2.text}
                </span>
              )}
              {data?.text && (
                <div
                  className="grid gap-4 md:gap-6 text-sm md:text-base 2xl:[&>p]:text-xl"
                  dangerouslySetInnerHTML={sanitizeHTML(data.text.text || "")}
                />
              )}
            </div>
            <div className="w-full">
              {data?.button?.href && (
                <div className="mt-6 text-left w-full">
                  <Link className="btn" href={data.button.href}>
                    {data.button.label}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
        {data?.images && data.images.length > 0 && (
          <div
            ref={logosRef}
            className="mt-8 md:mt-16 flex flex-wrap items-center justify-center md:justify-between gap-5 md:gap-8 2xl:gap-6"
          >
            {data.images.map((logo, index) => (
              <div
                key={index}
                className={`transition-all duration-700 ease-out grayscale hover:grayscale-0 flex-shrink-0 ${
                  logosVisible ? "opacity-100 scale-100" : "opacity-0 scale-90"
                }`}
                style={{
                  transitionDelay: logosVisible ? `${index * 100}ms` : "0ms",
                }}
              >
                {(() => {
                  const altText = logo?.images_id?.alt || "";
                  const isNarrowLogo = /american academy/i.test(altText);

                  return (
                <Image
                  src={getReadableImageUrl(
                    logo?.images_id?.image_file?.image?.id || "",
                    logo?.images_id?.image_file?.image?.filename_download || ""
                  )}
                  alt={altText}
                  width={220}
                  height={100}
                  className={`w-auto object-contain ${
                    isNarrowLogo
                      ? "h-9 md:h-16 lg:h-20"
                      : index === 3
                        ? "h-6 md:h-9 lg:h-11"
                        : "h-7 md:h-12 lg:h-16"
                  }`}
                  quality={90}
                  loading="lazy"
                />
                  );
                })()}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
